import { PauseCircleIcon } from '@phosphor-icons/react';
import type { LeaseableResourcePause } from '@erp/shared-types';
import { Badge } from 'src/components/ui/badge';
import { T, Var } from 'gt-react';
import {
  rejectionTimeAgo,
  useCredentialSurfaceLabel,
} from './credentialRejection';

/**
 * Shared presentation for an operator-reported credential rejection.
 *
 * Three surfaces render the same facts — the org settings resource list (which can also resume),
 * the per-agent resources tab (read-only) and the credentials list — so the badge and the wording
 * of the reason line live here rather than being re-invented in each. The `surface` vocabulary and
 * the relative-time helper are in `credentialRejection.ts` (components only in this file).
 */

/**
 * Replaces the plain "Inactive" badge for a resource an operator paused. Amber rather than grey:
 * an administrator's manual deactivation is a settled state, this one is waiting on a human.
 */
export function ResourcePausedBadge() {
  return (
    <Badge variant="warning" className="text-[10px] h-5 gap-1">
      <PauseCircleIcon className="h-3 w-3" weight="fill" />
      <T context="status badge for an agent resource paused by a rejected credential">
        Paused
      </T>
    </Badge>
  );
}

/** One line under a resource row saying why it is paused, and how long ago. */
export function ResourcePauseReason({
  pause,
}: {
  pause: LeaseableResourcePause;
}) {
  const surfaceLabel = useCredentialSurfaceLabel()(pause.surface);

  return (
    <p className="text-xs text-amber-700 dark:text-amber-400">
      <T context="reason line under an agent resource paused by a rejected credential">
        Credential rejected at the <Var>{surfaceLabel}</Var> sign-in &middot;{' '}
        <Var>{rejectionTimeAgo(pause.rejectedAt)}</Var>
      </T>
    </p>
  );
}
